import { routeMarketingEvent } from './marketingRouter';
import { getPageUrlValue, determinePaymentVersion } from '../utils/scripts/analyticsTags';
import { getFirstScreenTag } from '../utils/scripts/getStartPageTheme';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AnalyticsEvent = Record<string, any> & {
  event?: string;
  quiz_action?: string;
  quiz_question?: string;
  quiz_answer?: unknown;
};

type SendArgs = {
  event: AnalyticsEvent;
};

type AmplitudeReadiness = 'ready' | 'unavailable';

type QueuedAmplitudeEvent = [string, Record<string, unknown>];

// Amplitude грузится с CDN и резолвит window.ampReady только после init().promise
// (см. public/index.html). Пока readiness не выполнен, amplitude-события копятся в
// FIFO-очереди, иначе ранние события (page_load, onboarding_loading, AB_TEST) уходят
// до завершения attribution/session pipeline или теряются совсем.
let amplitudeReadiness: AmplitudeReadiness | null = null;
let amplitudeQueue: QueuedAmplitudeEvent[] = [];

function trackAmplitude(name: string, props: Record<string, unknown>): void {
  const amp = typeof window !== 'undefined' ? window.amplitude : undefined;
  if (amp && typeof amp.track === 'function') {
    amp.track(name, props);
  }
}

function settleAmplitudeReadiness(status: AmplitudeReadiness): void {
  if (amplitudeReadiness) return;
  amplitudeReadiness = status;

  const queued = amplitudeQueue;
  amplitudeQueue = [];

  // 'unavailable' — очередь дропается: ни retry, ни повторной буферизации.
  if (status === 'ready') {
    queued.forEach(([name, props]) => trackAmplitude(name, props));
  }
}

function sendToAmplitude(name: string, props: Record<string, unknown>): void {
  if (amplitudeReadiness === 'unavailable') return;
  if (amplitudeReadiness === 'ready') {
    trackAmplitude(name, props);
    return;
  }
  amplitudeQueue.push([name, props]);
}

function subscribeToAmplitudeReadiness(): void {
  const ready = typeof window !== 'undefined' ? window.ampReady : undefined;

  // Шелл без deferred (например закешированный старый index.html) — работаем как раньше:
  // очередь не держим, trackAmplitude сам проверит наличие window.amplitude.
  if (!ready || typeof ready.then !== 'function') {
    settleAmplitudeReadiness('ready');
    return;
  }

  ready.then(
    (status) => settleAmplitudeReadiness(status === 'unavailable' ? 'unavailable' : 'ready'),
    () => settleAmplitudeReadiness('unavailable'),
  );
}

subscribeToAmplitudeReadiness();

function normalizeAmplitudeProps(event: AnalyticsEvent): Record<string, unknown> {
  const props: Record<string, unknown> = { ...event };

  if ('quiz_answer' in props) {
    props.answer = props.quiz_answer;
    delete props.quiz_answer;
  }

  return props;
}

// Параметры, которые должны быть на КАЖДОМ событии:
//   link        — воронка + версия оплаты (тот же формат, что уходит в localStorage
//                 и на платёжную страницу, см. App.tsx и getLinkToPaymentPage.js);
//   first_screen — только для конфигов с тематическим первым экраном.
// Поля самого события перекрывают общие — если отправитель задал link явно,
// выигрывает его значение.
function withCommonProps(event: AnalyticsEvent): AnalyticsEvent {
  const common: AnalyticsEvent = { link: `${getPageUrlValue()}${determinePaymentVersion()}` };
  const firstScreen = getFirstScreenTag();
  if (firstScreen) common.first_screen = firstScreen;

  return { ...common, ...event };
}

export function send({ event }: SendArgs): void {
  if (!event || !event.event) {
    return;
  }

  const enrichedEvent = withCommonProps(event);
  let amplitudeEventName = event.event;
  const amplitudeProps = normalizeAmplitudeProps(enrichedEvent);

  if (event.event === 'quiz_funnel' && event.quiz_action) {
    amplitudeEventName = event.quiz_action;
    delete amplitudeProps.quiz_action;
    delete amplitudeProps.event;
  } else if (event.event === 'onboarding_question' && event.quiz_question) {
    amplitudeEventName = event.quiz_question;
    delete amplitudeProps.quiz_question;
    delete amplitudeProps.event;
  } else if (event.event === 'AB_TEST') {
    amplitudeEventName = 'AB_TEST';
    delete amplitudeProps.event;
  } else {
    delete amplitudeProps.event;
  }

  // Гейт readiness применяется только к amplitude-ветке.
  sendToAmplitude(amplitudeEventName, amplitudeProps);

  routeMarketingEvent(enrichedEvent);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(enrichedEvent);
}
